import { useState, useEffect } from 'react';
import { Check, Shield, CreditCard, Loader2 } from 'lucide-react';
import { eventInfo, venue } from '../../data/landingData';
import { Countdown, SpotsRemaining } from './Countdown';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Pricing = () => {
  const [paymentType, setPaymentType] = useState('full');
  const [isProcessing, setIsProcessing] = useState(false);
  const [spotsRemaining, setSpotsRemaining] = useState(eventInfo.spotsRemaining);

  // Check for return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    }

    // Fetch real spots remaining
    fetchSpotsRemaining();
  }, []);

  const fetchSpotsRemaining = async () => {
    try {
      const response = await fetch(`${API}/registrations/count`);
      if (response.ok) {
        const data = await response.json();
        setSpotsRemaining(data.spots_remaining);
      }
    } catch (error) {
      console.error('Error fetching registration count:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Payment status check timed out. Check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to The Reset, King!');
        // Clear the URL params
        window.history.replaceState({}, '', window.location.pathname);
        fetchSpotsRemaining();
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleRegister = async () => {
    setIsProcessing(true);

    try {
      const originUrl = window.location.origin;

      const response = await fetch(`${API}/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_type: paymentType,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <section 
      id="pricing" 
      className="py-24 px-6 bg-[#111111] relative overflow-hidden"
      data-testid="pricing-section"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.2em] text-xs font-bold mb-4">
            Investment In Yourself
          </p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white mb-4">
            Secure Your Transformation
          </h2>
          <p className="text-zinc-400 font-manrope max-w-2xl mx-auto mb-8">
            Join a brotherhood of men committed to growth, healing, and building legacy.
          </p>
          
          {/* Countdown */}
          <div className="mb-6">
            <p className="text-zinc-500 font-manrope text-sm mb-4">Retreat begins in:</p>
            <div className="flex justify-center">
              <Countdown />
            </div>
          </div>

          {/* Spots remaining */}
          <div className="flex justify-center">
            <SpotsRemaining spots={spotsRemaining} />
          </div>
        </div>

        {/* Pricing card */}
        <div className="max-w-lg mx-auto">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-3xl overflow-hidden gold-glow">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-6 border-b border-white/10">
              <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.2em] text-xs font-bold mb-2">
                Full Retreat Experience
              </p>
              <div className="flex items-baseline gap-2">
                <span className="font-cormorant text-5xl font-medium text-white">
                  ${eventInfo.price.toLocaleString()}
                </span>
                <span className="text-zinc-500 font-manrope">per person</span>
              </div>
            </div>

            {/* Payment toggle */}
            <div className="p-6 border-b border-white/10">
              <div className="flex gap-2 p-1 bg-[#1A1A1A] rounded-lg">
                <button
                  onClick={() => setPaymentType('full')}
                  className={`flex-1 py-3 px-4 rounded-md font-manrope text-sm font-medium transition-all ${
                    paymentType === 'full'
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="payment-toggle-full"
                >
                  Pay in Full
                </button>
                <button
                  onClick={() => setPaymentType('payment_plan')}
                  className={`flex-1 py-3 px-4 rounded-md font-manrope text-sm font-medium transition-all ${
                    paymentType === 'payment_plan'
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="payment-toggle-plan"
                >
                  3-Part Plan
                </button>
              </div>
              
              {paymentType === 'payment_plan' && (
                <p className="text-center text-[#D4AF37] font-manrope text-sm mt-4">
                  3 payments of <span className="font-bold">${eventInfo.paymentPlanAmount}/month</span>
                </p>
              )}
            </div>

            {/* What's included */}
            <div className="p-6 space-y-4">
              <p className="text-zinc-400 font-manrope text-sm uppercase tracking-wider mb-4">What's Included:</p>
              
              {venue.included.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-300 font-manrope">{item}</span>
                </div>
              ))}
              
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-zinc-300 font-manrope">All retreat activities & workshops</span>
              </div>
            </div>

            {/* CTA */}
            <div className="p-6 pt-0">
              <button
                onClick={handleRegister}
                disabled={isProcessing}
                className="block w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-xl font-manrope font-bold text-lg text-center hover:bg-[#B8860B] transition-all hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
                data-testid="register-cta"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  paymentType === 'full' 
                    ? `Register Now — $${eventInfo.price.toLocaleString()}`
                    : `Start with $${eventInfo.paymentPlanAmount} Today`
                )}
              </button>
              
              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 mt-4 text-zinc-500">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span className="font-manrope text-xs">Secure Payment</span>
                </div>
                <div className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="font-manrope text-xs">Powered by Stripe</span>
                </div>
              </div>
            </div>
          </div>

          {/* Cancellation policy */}
          <div className="mt-8 text-center">
            <p className="text-zinc-500 font-manrope text-sm">
              <span className="text-zinc-400">Cancellation Policy:</span> 25% fee applies. No refunds after May 1, 2026.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
